// src/store/middleware/autoSaveMiddleware.ts
/**
 * Auto-Save Middleware (T-23)
 * 
 * Redux middleware that automatically saves drafts when persistable
 * state changes. Implements debouncing and respects user preferences.
 */

import type { Middleware } from 'redux';
import type { RootState } from '../../types/redux';
import { debouncedSaveDraft, cancelDebouncedSave } from '../../lib/localPersistence';
import { completeSave, updateDraftMetadata } from '../slices/draftSlice';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Actions that should trigger auto-save
 * These are actions that modify persistable state
 */
const PERSISTABLE_ACTION_PREFIXES = [
  'viewport/',         // Pan, zoom, scale changes
  'numberLines/',      // Number line configuration
  'secondaryLine/',    // Secondary line settings
  'equivalentFractions/', // Equivalent fractions mode settings
  'expressions/',      // Expression objects
  'jumpIndicators/',   // Jump indicators
  'annotations/',      // Annotations (if enabled)
  'constants/',        // Constants display
  'labelVisibility/',  // Label visibility settings
  'customTicks/',      // Custom tick marks
];

/**
 * Actions that should NOT trigger auto-save
 * These are transient UI actions or actions that handle their own persistence
 */
const NON_PERSISTABLE_ACTION_PREFIXES = [
  'ui/',              // UI state (selections, dialogs, etc.)
  'share/',           // Share state (temporary)
  'draft/',           // Draft actions (would cause recursion)
  'preferences/',     // Preferences handle their own persistence
  'secondaryLine/',   // TEMPORARY FIX: Exclude secondary line to prevent freeze
];

/**
 * Specific actions that should trigger immediate metadata updates
 */
const METADATA_UPDATE_ACTIONS = [
  'draft/completeSave',
  'draft/clearDraft',
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if an action should trigger auto-save
 */
function shouldTriggerAutoSave(actionType: string): boolean {
  console.log('🔍 Checking if action should trigger auto-save:', actionType);
  
  // Explicitly excluded actions
  if (NON_PERSISTABLE_ACTION_PREFIXES.some(prefix => actionType.startsWith(prefix))) {
    console.log('❌ Action excluded:', actionType);
    return false;
  }

  // Explicitly included actions
  if (PERSISTABLE_ACTION_PREFIXES.some(prefix => actionType.startsWith(prefix))) {
    console.log('✅ Action included:', actionType);
    return true;
  }

  // Default to false for unknown actions
  console.log('❓ Unknown action, defaulting to false:', actionType);
  return false;
}

/**
 * Check if an action should trigger metadata update
 */
function shouldUpdateMetadata(actionType: string): boolean {
  return METADATA_UPDATE_ACTIONS.includes(actionType);
}

/**
 * Get current auto-save status from state
 */
function isAutoSaveEnabled(state: RootState): boolean {
  const enabled = state.preferences.autoSave && state.draft.autoSaveEnabled && !state.draft.suppressAutoSave;
  console.log('🔍 Auto-save check:', {
    preferencesAutoSave: state.preferences.autoSave,
    draftAutoSaveEnabled: state.draft.autoSaveEnabled,
    suppressAutoSave: state.draft.suppressAutoSave,
    result: enabled
  });
  return enabled;
}

// =============================================================================
// MIDDLEWARE IMPLEMENTATION
// =============================================================================

/**
 * Auto-save middleware that monitors state changes and triggers debounced saves
 */
export const autoSaveMiddleware: Middleware<{}, RootState> = (store) => (next) => (action: any) => {
  // Simple test to see if middleware is being called
  console.log('🎯 MIDDLEWARE CALLED with action:', action.type);
  
  // Execute the action first
  const result = next(action);
  
  try {
    const state = store.getState();
    const actionType = action.type;

    // Always log in development for debugging
    console.log(`🔍 Middleware processing action: ${actionType}`);

    // Handle metadata updates
    if (shouldUpdateMetadata(actionType)) {
      console.log('📝 Updating draft metadata');
      store.dispatch(updateDraftMetadata());
      return result;
    }

    // Check if auto-save is enabled
    if (!isAutoSaveEnabled(state)) {
      console.log(`⏸️ Auto-save disabled (pref: ${state.preferences.autoSave}, draft: ${state.draft.autoSaveEnabled})`);
      return result;
    }

    // Check if this action should trigger auto-save
    if (!shouldTriggerAutoSave(actionType)) {
      console.log(`⏸️ Action "${actionType}" should not trigger auto-save`);
      return result;
    }

    // Trigger debounced save
    console.log(`🚀 Auto-save triggered by action: ${actionType}`);
    debouncedSaveDraft(state, (result) => {
      // Dispatch completeSave action to update UI state
      store.dispatch(completeSave(result));
    });

  } catch (error) {
    // Don't break the app if auto-save fails
    console.warn('Auto-save middleware error:', error);
  }

  return result;
};

// =============================================================================
// MIDDLEWARE UTILITIES
// =============================================================================

/**
 * Middleware for handling app shutdown/cleanup
 * Cancels pending debounced saves and optionally triggers immediate save
 */
export const createShutdownMiddleware = (saveOnShutdown: boolean = true): Middleware<{}, RootState> => {
  return (store) => (next) => (action: any) => {
    // Handle page unload/refresh events
    if (action.type === '@@APP/SHUTDOWN' || action.type === '@@APP/PAGE_UNLOAD') {
      try {
        cancelDebouncedSave();
        
        if (saveOnShutdown) {
          const state = store.getState();
          if (isAutoSaveEnabled(state)) {
            // Immediate synchronous save on shutdown
                    import('../../lib/localPersistence').then(async ({ saveDraftToStorage }) => {
          try {
            const result = await saveDraftToStorage(state);
            
            if (!result.success) {
              console.warn('Shutdown save failed:', result.error);
            }
          } catch (error) {
            console.warn('Shutdown save failed:', error);
          }
        });
          }
        }
      } catch (error) {
        console.warn('Shutdown middleware error:', error);
      }
    }

    return next(action);
  };
};

/**
 * Enhanced auto-save middleware with action filtering and performance optimization
 */
export const createAutoSaveMiddleware = (options: {
  /** Enable detailed logging for debugging */
  enableLogging?: boolean;
  /** Custom action prefixes to include */
  customPersistableActions?: string[];
  /** Custom action prefixes to exclude */
  customNonPersistableActions?: string[];
  /** Minimum time between save triggers (ms) */
  minSaveInterval?: number;
} = {}): Middleware<{}, RootState> => {
  
  const {
    enableLogging = false,
    customPersistableActions = [],
    customNonPersistableActions = [],
    minSaveInterval = 1000,
  } = options;

  let lastSaveTime = 0;

  // Merge custom action lists with defaults
  const persistableActions = [...PERSISTABLE_ACTION_PREFIXES, ...customPersistableActions];
  const nonPersistableActions = [...NON_PERSISTABLE_ACTION_PREFIXES, ...customNonPersistableActions];

  return (store) => (next) => (action: any) => {
    const result = next(action);
    
    try {
      const state = store.getState();
      const actionType = action.type;
      const now = Date.now();

      // Handle metadata updates (always process these)
      if (shouldUpdateMetadata(actionType)) {
        store.dispatch(updateDraftMetadata());
        return result;
      }

      // Check if auto-save is enabled
      if (!isAutoSaveEnabled(state)) {
        if (enableLogging) {
          console.debug('Auto-save skipped: disabled in preferences');
        }
        return result;
      }

      // Rate limiting: don't trigger saves too frequently
      if (now - lastSaveTime < minSaveInterval) {
        if (enableLogging) {
          console.debug('Auto-save skipped: rate limited');
        }
        return result;
      }

      // Check if action should trigger save with custom lists
      const shouldSave = 
        persistableActions.some(prefix => actionType.startsWith(prefix)) &&
        !nonPersistableActions.some(prefix => actionType.startsWith(prefix));

      if (!shouldSave) {
        if (enableLogging) {
          console.debug('Auto-save skipped: non-persistable action:', actionType);
        }
        return result;
      }

      // Update last save time
      lastSaveTime = now;

      // Trigger debounced save
      debouncedSaveDraft(state);

      if (enableLogging) {
        console.debug('Auto-save triggered by:', actionType);
      }

    } catch (error) {
      console.warn('Enhanced auto-save middleware error:', error);
    }

    return result;
  };
};

// =============================================================================
// BROWSER EVENT HANDLERS
// =============================================================================

/**
 * Set up browser event listeners for auto-save on page unload
 */
export function setupAutoSaveEventListeners(store: any) {
  // Handle page refresh/close
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    try {
      const state = store.getState();
      if (isAutoSaveEnabled(state)) {
        // Trigger immediate save
        cancelDebouncedSave();
        
        import('../../lib/localPersistence').then(async ({ saveDraftToStorage }) => {
          try {
            await saveDraftToStorage(state);
          } catch (error) {
            console.warn('Before unload save failed:', error);
          }
        });

        // Show warning if there are unsaved changes
        if (state.draft.lastSaved) {
          const timeSinceLastSave = Date.now() - new Date(state.draft.lastSaved).getTime();
          if (timeSinceLastSave > 10000) { // 10 seconds
            event.preventDefault();
            event.returnValue = 'You have unsaved work. Are you sure you want to leave?';
            return event.returnValue;
          }
        }
      }
    } catch (error) {
      console.warn('Before unload handler error:', error);
    }
  };

  // Handle visibility change (tab switching)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      try {
        const state = store.getState();
        if (isAutoSaveEnabled(state)) {
          // Force save when tab becomes hidden
          cancelDebouncedSave();
          import('../../lib/localPersistence').then(async ({ saveDraftToStorage }) => {
            try {
              await saveDraftToStorage(state);
            } catch (error) {
              console.warn('Visibility change save failed:', error);
            }
          });
        }
      } catch (error) {
        console.warn('Visibility change handler error:', error);
      }
    }
  };

  // Add event listeners
  window.addEventListener('beforeunload', handleBeforeUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

// autoSaveMiddleware is already exported above with 'export const'
// No additional export needed here